import React, { useState } from 'react';
import type { ComponentConfig } from '@puckeditor/core';
import styled from 'styled-components';

import { toggleField } from '../fields/widgets/toggle-field';
import { BODY_TEXT_SIZES, FAQ_QUESTION_GAP, FAQ_QUESTION_INSET, FAQ_QUESTION_FONT_WEIGHT } from '../fields/tokens';
import { interpolateFromPuck } from '../interpolation';

import { starterChild } from './slot';
import type { SlotProps } from './slot';

export type CollapsibleContentProps = {
  heading: string;
  content?: React.FC<SlotProps>;
  openByDefault: boolean;
};

const STARTER_CONTENT = [starterChild('Paragraph', { text: '<p>Add your content here.</p>' })];

const Wrapper = styled.div`
  width: 100%;
  min-width: 0;
`;

const Heading = styled.h3`
  margin: 0;
`;

const Trigger = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${FAQ_QUESTION_GAP};
  width: 100%;
  padding: ${FAQ_QUESTION_INSET};
  border: 0;
  background: var(--dcm-cms-collapsible-background, transparent);
  color: var(--dcm-cms-collapsible-color, inherit);
  font-family: var(--dcm-cms-collapsible-font-family, inherit);
  font-size: ${BODY_TEXT_SIZES.xl};
  font-weight: ${FAQ_QUESTION_FONT_WEIGHT};
  text-align: left;
  cursor: pointer;

  > span:first-child {
    min-width: 0;
    overflow-wrap: anywhere;
  }

  > span:last-child {
    flex: none;
    transform: rotate(180deg);
  }

  &[aria-expanded='true'] > span:last-child {
    transform: none;
  }

  &:focus-visible {
    outline: var(--dcm-cms-collapsible-focus-outline, 3px solid var(--dcm-color-primary, #0b99e6));
    outline-offset: -3px;
  }
`;

function CollapsibleContent({
  id,
  heading,
  content,
  openByDefault,
  isEditing,
}: CollapsibleContentProps & { id: string; isEditing: boolean }): JSX.Element {
  const Slot = content;
  const [isOpen, setIsOpen] = useState((openByDefault as unknown) === true);
  const expanded = isEditing || isOpen;
  const bodyId = `cms-collapsible-${id}`;
  const handleToggle = (): void => setIsOpen((open) => !open);

  return (
    <Wrapper>
      <Heading>
        <Trigger
          type='button'
          aria-expanded={expanded}
          aria-controls={bodyId}
          disabled={isEditing}
          onClick={handleToggle}
        >
          <span>{heading}</span>
          <span aria-hidden='true'>⌃</span>
        </Trigger>
      </Heading>
      <div id={bodyId} hidden={!expanded}>
        {Slot && <Slot minEmptyHeight={80} />}
      </div>
    </Wrapper>
  );
}

export const collapsibleContentConfig: ComponentConfig<CollapsibleContentProps> = {
  label: 'Collapsible content',
  ai: {
    instructions:
      'A single expandable information block with a heading and a body slot. Add content blocks into the content slot. Use inside a Section for background and spacing. Keep openByDefault false for optional details; do not hide essential shopping information. The body stays visible while editing, regardless of the shopper default.',
  },
  fields: {
    heading: { type: 'text', label: 'Heading' },
    content: { type: 'slot' },
    openByDefault: toggleField('Open by default'),
  },
  defaultProps: {
    heading: 'More information',
    content: (STARTER_CONTENT as unknown) as React.FC<SlotProps>,
    openByDefault: false,
  },
  render: ({ id, heading = 'More information', content, openByDefault = false, puck }) => (
    <CollapsibleContent
      id={id}
      heading={interpolateFromPuck(heading, puck).trim() || 'More information'}
      content={content}
      openByDefault={openByDefault}
      isEditing={Boolean(puck.isEditing)}
    />
  ),
};
